import React, { useState, useEffect, useCallback, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useI18n } from "./i18n";
import {
  getReferralCode, getReferralStats,
  getMyReferrals, getMyCommissions,
  checkReferralCode, updateReferralCode,
} from "./accountClient";

/* ═══════════════════════════════════════════════════════════════════════════
   I18N — 6 langues
═══════════════════════════════════════════════════════════════════════════ */
const WT = {
  "title":      { fr:"Parrainage",                  en:"Referrals",                 es:"Referidos",                  de:"Empfehlungen",              pt:"Indicações",               ar:"الإحالات" },
  "title2":     { fr:"Gagnez en partageant",        en:"Earn by sharing",           es:"Gana compartiendo",          de:"Verdienen durch Teilen",    pt:"Ganhe compartilhando",     ar:"اكسب عبر المشاركة" },
  "sub":        { fr:"Invitez vos amis sur EVPTrading et touchez 5% de leurs retraits.",
                  en:"Invite friends to EVPTrading and earn 5% of their withdrawals.",
                  es:"Invita a tus amigos a EVPTrading y gana 5% de sus retiros.",
                  de:"Lade Freunde zu EVPTradingein und verdiene 5% ihrer Auszahlungen.",
                  pt:"Convide amigos para o EVPTrading e ganhe 5% dos saques deles.",
                  ar:"ادعُ أصدقاءك إلى EVPTradingواكسب 5٪ من سحوباتهم." },

  // Code section
  "code.title": { fr:"Votre code",                  en:"Your code",                 es:"Tu código",                  de:"Ihr Code",                  pt:"Seu código",               ar:"رمزك" },
  "code.copy":  { fr:"Copier",                      en:"Copy",                      es:"Copiar",                     de:"Kopieren",                  pt:"Copiar",                   ar:"نسخ" },
  "code.copied":{ fr:"Copié !",                     en:"Copied!",                   es:"¡Copiado!",                  de:"Kopiert!",                  pt:"Copiado!",                 ar:"تم النسخ!" },
  // Personnalisation du code
  'code.edit': { fr:'Personnaliser', en:'Customize', es:'Personalizar', de:'Anpassen', pt:'Personalizar', ar:'تخصيص' },
  'code.save': { fr:'Enregistrer', en:'Save', es:'Guardar', de:'Speichern', pt:'Salvar', ar:'حفظ' },
  'code.cancel': { fr:'Annuler', en:'Cancel', es:'Cancelar', de:'Abbrechen', pt:'Cancelar', ar:'إلغاء' },
  'code.saved': { fr:'Code mis à jour !', en:'Code updated!', es:'¡Código actualizado!', de:'Code aktualisiert!', pt:'Código atualizado!', ar:'تم تحديث الرمز!' },
  'code.hint': { fr:'4 à 12 caractères : lettres et chiffres uniquement.', en:'4 to 12 characters: letters and digits only.', es:'De 4 a 12 caracteres: solo letras y números.', de:'4 bis 12 Zeichen: nur Buchstaben und Ziffern.', pt:'4 a 12 caracteres: apenas letras e números.', ar:'من 4 إلى 12 حرفًا: أحرف وأرقام فقط.' },
  'code.warn': { fr:'Votre ancien lien de partage cessera de fonctionner.', en:'Your previous share link will stop working.', es:'Tu enlace anterior dejará de funcionar.', de:'Ihr bisheriger Link funktioniert dann nicht mehr.', pt:'Seu link anterior deixará de funcionar.', ar:'سيتوقف رابط المشاركة السابق عن العمل.' },
  'code.checking': { fr:'Vérification…', en:'Checking…', es:'Comprobando…', de:'Prüfung…', pt:'Verificando…', ar:'جارٍ التحقق…' },
  'code.available': { fr:'✓ Disponible', en:'✓ Available', es:'✓ Disponible', de:'✓ Verfügbar', pt:'✓ Disponível', ar:'✓ متاح' },
  'code.err.taken': { fr:'✗ Déjà utilisé par un autre utilisateur', en:'✗ Already used by someone else', es:'✗ Ya lo usa otro usuario', de:'✗ Bereits vergeben', pt:'✗ Já está em uso', ar:'✗ مستخدم بالفعل' },
  'code.err.too_short': { fr:'✗ Trop court (4 caractères minimum)', en:'✗ Too short (4 characters minimum)', es:'✗ Demasiado corto (mínimo 4)', de:'✗ Zu kurz (mindestens 4)', pt:'✗ Muito curto (mínimo 4)', ar:'✗ قصير جدًا (4 أحرف على الأقل)' },
  'code.err.too_long': { fr:'✗ Trop long (12 caractères maximum)', en:'✗ Too long (12 characters maximum)', es:'✗ Demasiado largo (máximo 12)', de:'✗ Zu lang (maximal 12)', pt:'✗ Muito longo (máximo 12)', ar:'✗ طويل جدًا (12 حرفًا كحد أقصى)' },
  'code.err.invalid_chars': { fr:'✗ Lettres et chiffres uniquement', en:'✗ Letters and digits only', es:'✗ Solo letras y números', de:'✗ Nur Buchstaben und Ziffern', pt:'✗ Apenas letras e números', ar:'✗ أحرف وأرقام فقط' },
  'code.err.reserved': { fr:'✗ Ce code est réservé', en:'✗ This code is reserved', es:'✗ Este código está reservado', de:'✗ Dieser Code ist reserviert', pt:'✗ Este código é reservado', ar:'✗ هذا الرمز محجوز' },
  'code.err.unchanged': { fr:"C'est déjà votre code actuel", en:'This is already your current code', es:'Ya es tu código actual', de:'Das ist bereits Ihr aktueller Code', pt:'Este já é o seu código atual', ar:'هذا هو رمزك الحالي' },
  'code.err.inactive': { fr:'✗ Votre code est désactivé', en:'✗ Your code is disabled', es:'✗ Tu código está desactivado', de:'✗ Ihr Code ist deaktiviert', pt:'✗ Seu código está desativado', ar:'✗ رمزك معطّل' },
  'code.err.generic': { fr:"✗ Impossible d'enregistrer. Réessayez.", en:'✗ Could not save. Try again.', es:'✗ No se pudo guardar. Inténtalo de nuevo.', de:'✗ Speichern fehlgeschlagen. Erneut versuchen.', pt:'✗ Não foi possível salvar. Tente novamente.', ar:'✗ تعذر الحفظ. حاول مرة أخرى.' },
  "url.label":  { fr:"Lien de partage",             en:"Share link",                es:"Enlace para compartir",      de:"Teilen-Link",               pt:"Link de compartilhamento", ar:"رابط المشاركة" },

  // Stats cards
  "stat.referrals":   { fr:"Filleuls totaux",       en:"Total referrals",           es:"Referidos totales",          de:"Empfehlungen gesamt",       pt:"Indicações totais",        ar:"إجمالي الإحالات" },
  "stat.qualifying":  { fr:"Filleuls qualifiés",    en:"Qualifying referrals",      es:"Referidos calificados",      de:"Qualifizierte",             pt:"Indicações qualificadas",  ar:"إحالات مؤهلة" },
  "stat.paid":        { fr:"Commissions payées",    en:"Commissions paid",          es:"Comisiones pagadas",         de:"Bezahlte Provisionen",      pt:"Comissões pagas",          ar:"عمولات مدفوعة" },
  "stat.pending":     { fr:"En attente (30j)",      en:"Pending (30d)",             es:"Pendientes (30d)",           de:"Ausstehend (30T)",          pt:"Pendentes (30d)",          ar:"قيد الانتظار (30 يومًا)" },

  // Monthly cap
  "cap.title":  { fr:"Plafond mensuel",             en:"Monthly cap",               es:"Límite mensual",             de:"Monatslimit",               pt:"Limite mensal",            ar:"الحد الشهري" },
  "cap.earned": { fr:"Gagné ce mois",               en:"Earned this month",         es:"Ganado este mes",            de:"Diesen Monat verdient",     pt:"Ganho este mês",           ar:"المكتسب هذا الشهر" },
  "cap.remain": { fr:"Restant",                     en:"Remaining",                 es:"Restante",                   de:"Verbleibend",               pt:"Restante",                 ar:"المتبقي" },

  // How it works
  "how.title":  { fr:"Comment ça marche",           en:"How it works",              es:"Cómo funciona",              de:"So funktioniert es",        pt:"Como funciona",            ar:"كيف يعمل" },
  "how.1.t":    { fr:"1. Partagez votre lien",      en:"1. Share your link",        es:"1. Comparte tu enlace",      de:"1. Link teilen",            pt:"1. Compartilhe seu link",  ar:"1. شارك رابطك" },
  "how.1.d":    { fr:"Envoyez-le sur vos réseaux sociaux, par message, par email…",
                  en:"Send it on social media, in messages, by email…",
                  es:"Envíalo en redes sociales, mensajes, correo…",
                  de:"Verschicke ihn in sozialen Medien, Nachrichten, E-Mails…",
                  pt:"Envie nas redes sociais, mensagens, e-mail…",
                  ar:"أرسله عبر وسائل التواصل والرسائل والبريد الإلكتروني…" },
  "how.2.t":    { fr:"2. Vos filleuls s'inscrivent", en:"2. Friends sign up",       es:"2. Tus amigos se registran", de:"2. Freunde melden sich an", pt:"2. Seus amigos se cadastram", ar:"2. يسجّل أصدقاؤك" },
  "how.2.d":    { fr:"Le code est appliqué automatiquement à l'inscription.",
                  en:"The code is applied automatically at signup.",
                  es:"El código se aplica automáticamente al registrarse.",
                  de:"Der Code wird bei der Anmeldung automatisch angewendet.",
                  pt:"O código é aplicado automaticamente no cadastro.",
                  ar:"يُطبَّق الرمز تلقائيًا عند التسجيل." },
  "how.3.t":    { fr:"3. Ils tradent et retirent",   en:"3. They trade and withdraw", es:"3. Operan y retiran",      de:"3. Sie traden und heben ab", pt:"3. Eles operam e sacam",  ar:"3. يتداولون ويسحبون" },
  "how.3.d":    { fr:"Ils doivent : vérifier leur téléphone, faire ≥10 trades sur ≥3 jours.",
                  en:"They must: verify phone, ≥10 trades over ≥3 days.",
                  es:"Deben: verificar teléfono, ≥10 operaciones en ≥3 días.",
                  de:"Sie müssen: Telefon verifizieren, ≥10 Trades über ≥3 Tage.",
                  pt:"Eles devem: verificar telefone, ≥10 operações em ≥3 dias.",
                  ar:"يجب: التحقق من الهاتف، ≥10 صفقات على ≥3 أيام." },
  "how.4.t":    { fr:"4. Vous touchez 5%",          en:"4. You earn 5%",            es:"4. Recibes 5%",              de:"4. Sie verdienen 5%",        pt:"4. Você ganha 5%",        ar:"4. تحصل على 5٪" },
  "how.4.d":    { fr:"Crédité automatiquement sur votre wallet 30 jours après leur retrait.",
                  en:"Credited automatically to your wallet 30 days after their withdrawal.",
                  es:"Acreditado automáticamente en tu wallet 30 días después de su retiro.",
                  de:"Wird 30 Tage nach deren Auszahlung automatisch gutgeschrieben.",
                  pt:"Creditado automaticamente na sua carteira 30 dias após o saque deles.",
                  ar:"يُضاف تلقائيًا إلى محفظتك بعد 30 يومًا من سحبهم." },

  // Referrals list
  "ref.list":   { fr:"Mes filleuls",                en:"My referrals",              es:"Mis referidos",              de:"Meine Empfehlungen",        pt:"Minhas indicações",        ar:"إحالاتي" },
  "ref.empty":  { fr:"Aucun filleul pour le moment.",en:"No referrals yet.",        es:"Aún no hay referidos.",      de:"Noch keine Empfehlungen.",  pt:"Ainda sem indicações.",    ar:"لا توجد إحالات بعد." },
  "ref.qualified":{fr:"✓ Qualifié",                 en:"✓ Qualified",               es:"✓ Calificado",               de:"✓ Qualifiziert",            pt:"✓ Qualificado",            ar:"✓ مؤهل" },
  "ref.notqual":{ fr:"En cours",                    en:"In progress",               es:"En proceso",                 de:"In Bearbeitung",            pt:"Em andamento",             ar:"قيد التقدم" },
  "ref.phone":  { fr:"Tél",                         en:"Phone",                     es:"Tel",                        de:"Tel",                       pt:"Tel",                      ar:"هاتف" },
  "ref.trades": { fr:"Trades",                      en:"Trades",                    es:"Operaciones",                de:"Trades",                    pt:"Operações",                ar:"الصفقات" },
  "ref.days":   { fr:"Jours actifs",                en:"Active days",               es:"Días activos",               de:"Aktive Tage",               pt:"Dias ativos",              ar:"أيام النشاط" },
  "ref.flagged":{ fr:"⚠ Suspect",                   en:"⚠ Flagged",                 es:"⚠ Marcado",                  de:"⚠ Markiert",                pt:"⚠ Marcado",                ar:"⚠ مشكوك" },

  // Commissions list
  "com.list":   { fr:"Mes commissions",             en:"My commissions",            es:"Mis comisiones",             de:"Meine Provisionen",         pt:"Minhas comissões",         ar:"عمولاتي" },
  "com.empty":  { fr:"Aucune commission pour le moment.",en:"No commissions yet.",   es:"Aún no hay comisiones.",     de:"Noch keine Provisionen.",   pt:"Ainda sem comissões.",     ar:"لا توجد عمولات بعد." },
  "com.pending":{ fr:"En attente",                  en:"Pending",                   es:"Pendiente",                  de:"Ausstehend",                pt:"Pendente",                 ar:"قيد الانتظار" },
  "com.approved":{ fr:"Versée",                     en:"Paid",                      es:"Pagada",                     de:"Bezahlt",                   pt:"Paga",                     ar:"مدفوعة" },
  "com.rejected":{ fr:"Rejetée",                    en:"Rejected",                  es:"Rechazada",                  de:"Abgelehnt",                 pt:"Rejeitada",                ar:"مرفوضة" },
  "com.until":  { fr:"Dispo le",                    en:"Available on",              es:"Disponible el",              de:"Verfügbar am",              pt:"Disponível em",            ar:"متاحة في" },
  "com.from":   { fr:"de",                          en:"from",                      es:"de",                         de:"von",                       pt:"de",                       ar:"من" },

  "loading":    { fr:"Chargement…",                 en:"Loading…",                  es:"Cargando…",                  de:"Lädt…",                     pt:"Carregando…",              ar:"جاري التحميل…" },
};

function wt(key, lang) {
  return WT[key]?.[lang] ?? WT[key]?.fr ?? key;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Styled-components
═══════════════════════════════════════════════════════════════════════════ */
const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;

const Wrap = styled.div`
  max-width: 1100px; margin: 0 auto;
  padding: 2rem 1.25rem 4rem;
  animation: ${fadeIn} .3s ease;
`;
const Header = styled.div`margin-bottom: 1.75rem;`;
const PageTitle = styled.h1`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: clamp(1.6rem, 4vw, 2.2rem);
  font-weight: 700; letter-spacing: -.025em;
  span { color: ${({theme}) => theme.gold}; }
`;
const PageSub = styled.p`
  font-size: .88rem; color: ${({theme}) => theme.textMuted};
  margin-top: .35rem; line-height: 1.5;
`;

/* Code card — vedette */
const CodeCard = styled.div`
  background: linear-gradient(135deg, ${({theme}) => theme.bgCard}, #1a1611);
  border: 1px solid ${({theme}) => theme.gold}44;
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  display: grid; gap: 1rem;
  @media (min-width: 720px) { grid-template-columns: 1fr 1fr; }
`;
const CodeBlock = styled.div`display: flex; flex-direction: column; gap: .35rem;`;
const FieldLabel = styled.span`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .62rem; letter-spacing: .08em;
  color: ${({theme}) => theme.textMuted};
  text-transform: uppercase;
`;
const CodeRow = styled.div`
  display: flex; align-items: center; gap: .5rem;
  background: ${({theme}) => theme.bg};
  border: 1px solid ${({theme}) => theme.border};
  border-radius: 10px;
  padding: .7rem .9rem;
`;
const CodeValue = styled.code`
  flex: 1;
  font-family: ${({theme}) => theme.fontMono};
  font-size: 1rem; font-weight: 700;
  color: ${({theme}) => theme.gold};
  letter-spacing: .12em;
  overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap;
    @media (max-width: 720px) { white-space: normal; font-size: .7rem; }

`;
const CopyBtn = styled.button`
  padding: .35rem .65rem; border-radius: 6px;
  border: 1px solid ${({theme}) => theme.borderLight};
  background: transparent; color: ${({theme}) => theme.text};
  font-family: ${({theme}) => theme.fontMono};
  font-size: .62rem; font-weight: 600;
  cursor: pointer; transition: all .15s;
  &:hover { background: ${({theme}) => theme.bgHover}; }
`;

/* Édition du code */
const EditInput = styled.input`
  flex: 1; min-width: 0; background: transparent; border: none; outline: none;
  font-family: ${({theme}) => theme.fontMono};
  font-size: 1rem; font-weight: 700; letter-spacing: .12em; text-transform: uppercase;
  color: ${({theme}) => theme.gold};
  &::placeholder { color: ${({theme}) => theme.textMuted}; letter-spacing: .05em; font-weight: 400; }
  @media (max-width: 720px) { font-size: 16px; }
`;
const EditRow = styled(CodeRow)`
  border-color: ${({$state, theme}) =>
    $state === "ok" ? theme.green : $state === "bad" ? theme.red : theme.borderLight};
  transition: border-color .15s;
`;
const BtnRow = styled.div`display: flex; gap: .4rem; flex-wrap: wrap; margin-top: .15rem;`;
const PrimaryBtn = styled.button`
  padding: .4rem .85rem; border-radius: 6px; border: none;
  background: ${({theme}) => theme.gold}; color: #000;
  font-family: ${({theme}) => theme.fontMono}; font-size: .66rem; font-weight: 700;
  cursor: pointer; &:disabled { opacity: .35; cursor: not-allowed; }
`;
const StatusLine = styled.div`
  min-height: 1.1rem; font-family: ${({theme}) => theme.fontMono}; font-size: .68rem;
  color: ${({$state, theme}) => $state === "ok" ? theme.green : $state === "bad" ? theme.red : theme.textMuted};
`;
const Hint = styled.div`font-size: .66rem; color: ${({theme}) => theme.textMuted}; line-height: 1.45;`;

/* Stats grid */
const StatsGrid = styled.div`
  display: grid; gap: .75rem;
  grid-template-columns: repeat(2, 1fr);
  @media (min-width: 720px) { grid-template-columns: repeat(4, 1fr); }
  margin-bottom: 1.5rem;
`;
const Stat = styled.div`
  background: ${({theme}) => theme.bgCard};
  border: 1px solid ${({theme}) => theme.border};
  border-radius: 10px;
  padding: 1rem;
  display: flex; flex-direction: column; gap: .25rem;
`;
const StatValue = styled.div`
  font-family: ${({theme}) => theme.fontMono};
  font-size: 1.4rem; font-weight: 700;
  color: ${({theme, $c}) =>
    $c === "green" ? theme.green :
    $c === "gold"  ? theme.gold  :
    $c === "accent"? theme.accent:
                      theme.text};
`;
const StatLabel = styled.div`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .6rem; letter-spacing: .08em;
  color: ${({theme}) => theme.textMuted};
  text-transform: uppercase;
`;

/* Cap progress */
const CapCard = styled.div`
  background: ${({theme}) => theme.bgCard};
  border: 1px solid ${({theme}) => theme.border};
  border-radius: 10px;
  padding: 1rem 1.2rem;
  margin-bottom: 1.5rem;
`;
const CapHead = styled.div`
  display: flex; justify-content: space-between; align-items: baseline;
  margin-bottom: .65rem;
`;
const CapTitle = styled.div`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .68rem; letter-spacing: .06em;
  color: ${({theme}) => theme.textMuted};
  text-transform: uppercase;
`;
const CapValue = styled.div`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .88rem; color: ${({theme}) => theme.text};
  & strong { color: ${({theme}) => theme.green}; font-weight: 700; }
`;
const CapBar = styled.div`
  height: 6px; border-radius: 100px;
  background: ${({theme}) => theme.bg};
  overflow: hidden;
`;
const CapFill = styled.div`
  height: 100%; border-radius: 100px;
  width: ${({$pct}) => Math.min(100, $pct)}%;
  background: linear-gradient(90deg, ${({theme}) => theme.green}, ${({theme}) => theme.gold});
  transition: width .4s ease;
`;

/* How it works */
const HowGrid = styled.div`
  display: grid; gap: .75rem;
  grid-template-columns: 1fr;
  @media (min-width: 720px) { grid-template-columns: repeat(4, 1fr); }
  margin-bottom: 1.5rem;
`;
const HowStep = styled.div`
  background: ${({theme}) => theme.bgCard};
  border: 1px solid ${({theme}) => theme.border};
  border-radius: 10px;
  padding: 1rem;
  display: flex; flex-direction: column; gap: .35rem;
`;
const HowStepTitle = styled.div`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .72rem; font-weight: 700;
  color: ${({theme}) => theme.accent};
  letter-spacing: .03em;
`;
const HowStepDesc = styled.p`
  font-size: .72rem; color: ${({theme}) => theme.textDim};
  line-height: 1.5;
`;

/* Tables */
const Section = styled.div`margin-bottom: 1.5rem;`;
const SectionTitle = styled.h2`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: 1rem; font-weight: 700;
  letter-spacing: -.01em;
  margin-bottom: .65rem;
`;
const Table = styled.div`
  background: ${({theme}) => theme.bgCard};
  border: 1px solid ${({theme}) => theme.border};
  border-radius: 10px;
  overflow: hidden;
`;
const TRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr 1.2fr;
  gap: .5rem;
  padding: .75rem 1rem;
  border-bottom: 1px solid ${({theme}) => theme.border};
  font-size: .77rem;
  &:last-child { border-bottom: none; }
  @media (max-width: 600px) {
    grid-template-columns: 1fr 1fr;
    & > :nth-child(3) { grid-column: 1/-1; padding-top: .25rem; color: #5a6a7a; font-size: .68rem; }
  }
`;
const TCell = styled.div`
  display: flex; align-items: center; gap: .35rem;
  font-family: ${({$mono, theme}) => $mono ? theme.fontMono : "inherit"};
  color: ${({theme, $dim}) => $dim ? theme.textMuted : theme.text};
`;
const Empty = styled.div`
  padding: 2.5rem; text-align: center;
  color: ${({theme}) => theme.textMuted};
  font-size: .82rem;
`;
const Pill = styled.span`
  display: inline-flex; align-items: center; gap: .25rem;
  padding: .15rem .55rem; border-radius: 100px;
  font-family: ${({theme}) => theme.fontMono};
  font-size: .62rem; font-weight: 600;
  background: ${({$c, theme}) =>
    $c === "green" ? `${theme.green}1a` :
    $c === "gold"  ? `${theme.gold}1a`  :
    $c === "red"   ? `${theme.red}1a`   :
                      theme.bgHover};
  color: ${({$c, theme}) =>
    $c === "green" ? theme.green :
    $c === "gold"  ? theme.gold  :
    $c === "red"   ? theme.red   :
                      theme.textMuted};
  border: 1px solid ${({$c, theme}) =>
    $c === "green" ? `${theme.green}55` :
    $c === "gold"  ? `${theme.gold}55`  :
    $c === "red"   ? `${theme.red}55`   :
                      theme.border};
`;

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════════════════════ */
export default function ReferralPage({ user }) {
  const { lang } = useI18n();
  const t = (k) => wt(k, lang);
  const dir = lang === "ar" ? "rtl" : "ltr";

  const [code,        setCode]        = useState(null);
  const [stats,       setStats]       = useState(null);
  const [referrals,   setReferrals]   = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [copied,      setCopied]      = useState(null);

  // ── Personnalisation du code ──
  const [editing,  setEditing]  = useState(false);
  const [draft,    setDraft]    = useState("");
  const [check,    setCheck]    = useState({ state: "idle", reason: null }); // idle | checking | ok | bad
  const [saving,   setSaving]   = useState(false);
  const [saveErr,  setSaveErr]  = useState("");
  const [justSaved,setJustSaved]= useState(false);
  const reqId = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, r, com] = await Promise.all([
        getReferralCode(),
        getReferralStats(),
        getMyReferrals(),
        getMyCommissions(),
      ]);
      setCode(c.data);
      setStats(s.data);
      setReferrals(r.data || []);
      setCommissions(com.data || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const cleanCode = (v) => v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, code?.max_length || 12);
  const minLen = code?.min_length || 4;

  // Vérification en direct (debounce 400 ms) — seule la dernière réponse compte
  useEffect(() => {
    if (!editing) return;
    setSaveErr("");
    if (!draft) { setCheck({ state: "idle", reason: null }); return; }
    if (draft === code?.code) { setCheck({ state: "bad", reason: "unchanged" }); return; }
    if (draft.length < minLen) { setCheck({ state: "bad", reason: "too_short" }); return; }
    setCheck({ state: "checking", reason: null });
    const id = ++reqId.current;
    const timer = setTimeout(async () => {
      try {
        const r = await checkReferralCode(draft);
        if (id !== reqId.current) return;
        setCheck(r.data.available ? { state: "ok", reason: null } : { state: "bad", reason: r.data.reason || "taken" });
      } catch {
        if (id === reqId.current) setCheck({ state: "bad", reason: "generic" });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [draft, editing, code?.code, minLen]);

  const startEdit = () => { setDraft(code?.code || ""); setCheck({ state: "bad", reason: "unchanged" }); setSaveErr(""); setEditing(true); };
  const cancelEdit = () => { reqId.current++; setEditing(false); setDraft(""); setSaveErr(""); };

  const saveCode = async () => {
    if (check.state !== "ok" || saving) return;
    setSaving(true); setSaveErr("");
    try {
      const r = await updateReferralCode(draft);
      setCode(r.data);
      setEditing(false); setDraft("");
      setJustSaved(true); setTimeout(() => setJustSaved(false), 2500);
    } catch (e) {
      const reason = e?.response?.data?.reason;
      if (reason) setCheck({ state: "bad", reason });          // ex. quelqu'un l'a pris entre-temps
      else setSaveErr(t("code.err.generic"));
    } finally { setSaving(false); }
  };

  const statusText =
    check.state === "checking" ? t("code.checking") :
    check.state === "ok"       ? t("code.available") :
    check.state === "bad"      ? t(`code.err.${check.reason}`) !== `code.err.${check.reason}` ? t(`code.err.${check.reason}`) : t("code.err.generic") :
    "";

  const copy = (txt, key) => {
    navigator.clipboard.writeText(txt).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  if (!user) {
    return <Wrap><Empty>Login required.</Empty></Wrap>;
  }
  if (loading) {
    return <Wrap><Empty>{t("loading")}</Empty></Wrap>;
  }

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric"
  }) : "—";

  return (
    <Wrap dir={dir}>
      <Header>
        <PageTitle>{t("title")} — <span>{t("title2")}</span></PageTitle>
        <PageSub>{t("sub")}</PageSub>
      </Header>

      {/* ─── Code card ─── */}
      {code && (
        <CodeCard>
          <CodeBlock>
            <FieldLabel>{t("code.title")}</FieldLabel>
            {!editing ? (
              <>
                <CodeRow>
                  <CodeValue>{code.code}</CodeValue>
                  <CopyBtn onClick={() => copy(code.code, "code")}>
                    {copied === "code" ? "✓ " + t("code.copied") : t("code.copy")}
                  </CopyBtn>
                  {code.can_edit !== false && <CopyBtn onClick={startEdit}>✎ {t("code.edit")}</CopyBtn>}
                </CodeRow>
                {justSaved && <StatusLine $state="ok">✓ {t("code.saved")}</StatusLine>}
              </>
            ) : (
              <>
                <EditRow $state={check.state === "ok" ? "ok" : check.state === "bad" && check.reason !== "unchanged" ? "bad" : ""}>
                  <EditInput
                    autoFocus value={draft} maxLength={code.max_length || 12}
                    autoCapitalize="characters" autoComplete="off" spellCheck={false}
                    aria-invalid={check.state === "bad" && check.reason !== "unchanged"}
                    onChange={(e) => setDraft(cleanCode(e.target.value))}
                    onKeyDown={(e) => { if (e.key === "Enter") saveCode(); if (e.key === "Escape") cancelEdit(); }}
                  />
                  <span style={{fontFamily:"DM Mono,monospace",fontSize:".6rem",color:"#5a6a7a"}}>{draft.length}/{code.max_length || 12}</span>
                </EditRow>
                <StatusLine $state={check.state === "ok" ? "ok" : check.state === "bad" && check.reason !== "unchanged" ? "bad" : ""} aria-live="polite">
                  {saveErr || statusText}
                </StatusLine>
                <Hint>{t("code.hint")} {t("code.warn")}</Hint>
                <BtnRow>
                  <PrimaryBtn onClick={saveCode} disabled={check.state !== "ok" || saving}>
                    {saving ? "…" : t("code.save")}
                  </PrimaryBtn>
                  <CopyBtn onClick={cancelEdit}>{t("code.cancel")}</CopyBtn>
                </BtnRow>
              </>
            )}
          </CodeBlock>
          <CodeBlock>
            <FieldLabel>{t("url.label")}</FieldLabel>
            <CodeRow>
              <CodeValue style={{fontSize:".82rem",letterSpacing:0}}>{code.share_url}</CodeValue>
              <CopyBtn onClick={() => copy(code.share_url, "url")}>
                {copied === "url" ? "✓ " + t("code.copied") : t("code.copy")}
              </CopyBtn>
            </CodeRow>
          </CodeBlock>
        </CodeCard>
      )}

      {/* ─── Stats ─── */}
      {stats && (
        <StatsGrid>
          <Stat>
            <StatValue>{stats.total_referrals}</StatValue>
            <StatLabel>{t("stat.referrals")}</StatLabel>
          </Stat>
          <Stat>
            <StatValue $c="green">{stats.qualifying_referrals}</StatValue>
            <StatLabel>{t("stat.qualifying")}</StatLabel>
          </Stat>
          <Stat>
            <StatValue $c="green">${stats.commissions_paid_usd}</StatValue>
            <StatLabel>{t("stat.paid")}</StatLabel>
          </Stat>
          <Stat>
            <StatValue $c="gold">${stats.commissions_pending_usd}</StatValue>
            <StatLabel>{t("stat.pending")}</StatLabel>
          </Stat>
        </StatsGrid>
      )}

      {/* ─── Monthly cap ─── */}
      {stats && (
        <CapCard>
          <CapHead>
            <CapTitle>{t("cap.title")}</CapTitle>
            <CapValue>
              <strong>${stats.this_month_earned_usd}</strong> / ${stats.monthly_cap_usd}
            </CapValue>
          </CapHead>
          <CapBar>
            <CapFill $pct={stats.month_progress_pct} />
          </CapBar>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:".4rem",
            fontSize:".66rem",color:"#5a6a7a",fontFamily:"DM Mono,monospace"}}>
            <span>{t("cap.earned")} : ${stats.this_month_earned_usd}</span>
            <span>{t("cap.remain")} : ${stats.this_month_remaining_usd}</span>
          </div>
        </CapCard>
      )}

      {/* ─── How it works ─── */}
      <Section>
        <SectionTitle>{t("how.title")}</SectionTitle>
        <HowGrid>
          {[1, 2, 3, 4].map((n) => (
            <HowStep key={n}>
              <HowStepTitle>{t(`how.${n}.t`)}</HowStepTitle>
              <HowStepDesc>{t(`how.${n}.d`)}</HowStepDesc>
            </HowStep>
          ))}
        </HowGrid>
      </Section>

      {/* ─── Referrals list ─── */}
      <Section>
        <SectionTitle>{t("ref.list")} ({referrals.length})</SectionTitle>
        <Table>
          {referrals.length === 0 ? (
            <Empty>{t("ref.empty")}</Empty>
          ) : referrals.map((r) => (
            <TRow key={r.id}>
              <TCell $mono>
                {r.username}
                {r.flagged && <Pill $c="red" style={{marginLeft:".4rem"}}>{t("ref.flagged")}</Pill>}
              </TCell>
              <TCell>
                {r.is_qualifying
                  ? <Pill $c="green">{t("ref.qualified")}</Pill>
                  : <Pill $c="gold">{t("ref.notqual")}</Pill>}
              </TCell>
              <TCell $mono $dim style={{fontSize:".68rem"}}>
                {t("ref.phone")} {r.phone_verified ? "✓" : "✗"} ·{" "}
                {t("ref.trades")} {r.trades_count}/{r.trades_required} ·{" "}
                {t("ref.days")} {r.active_days}/{r.days_required}
              </TCell>
            </TRow>
          ))}
        </Table>
      </Section>

      {/* ─── Commissions list ─── */}
      <Section>
        <SectionTitle>{t("com.list")} ({commissions.length})</SectionTitle>
        <Table>
          {commissions.length === 0 ? (
            <Empty>{t("com.empty")}</Empty>
          ) : commissions.map((c) => (
            <TRow key={c.id}>
              <TCell $mono>
                {t("com.from")} {c.referee}
              </TCell>
              <TCell $mono $c={c.status === "APPROVED" ? "green" : c.status === "REJECTED" ? "red" : "gold"}>
                ${c.commission_amount_usd}
              </TCell>
              <TCell $dim style={{fontSize:".68rem"}}>
                <Pill $c={c.status === "APPROVED" ? "green" : c.status === "REJECTED" ? "red" : "gold"}>
                  {c.status === "PENDING"  ? t("com.pending")  :
                   c.status === "APPROVED" ? t("com.approved") :
                                              t("com.rejected")}
                </Pill>
                {c.status === "PENDING" && <span style={{marginLeft:".4rem"}}>· {t("com.until")} {fmt(c.available_at)}</span>}
                {c.status === "APPROVED" && c.paid_at && <span style={{marginLeft:".4rem"}}>· {fmt(c.paid_at)}</span>}
                {c.status === "REJECTED" && c.reject_reason && <span style={{marginLeft:".4rem"}}>· {c.reject_reason}</span>}
              </TCell>
            </TRow>
          ))}
        </Table>
      </Section>
    </Wrap>
  );
}
